import { useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";

import type { AppBrandingDetailsDto } from "@/api/types";
import { ConnectionFailurePage } from "@/components/Error/ConnectionFailurePage";
import { useProjectAppBranding } from "@/hooks/Network/useProjectAppBranding";
import { useConfigurationQueries } from "@/queries/configurations/queryOptions";

import { DocumentBranding } from "./DocumentBranding";
import { StartupLoading } from "./StartupLoading";

type ProjectAppBrandingConfigContext = {
  appBrandingConfig: AppBrandingDetailsDto;
};

const ProjectAppBrandingConfigContext = createContext<ProjectAppBrandingConfigContext | null>(null);

export function useProjectAppBrandingConfig(): ProjectAppBrandingConfigContext {
  const context = useContext(ProjectAppBrandingConfigContext);

  if (!context) {
    throw new Error("useProjectAppBrandingConfig must be used within an ProjectAppBrandingConfigProvider");
  }

  return context;
}

type ProjectAppBrandingConfigProviderProps = {
  children: React.ReactNode;
};

/** Exposes the root project brand catalog entry and applies its document theme. Request scope is set by ProjectLoader. */
export function ProjectAppBrandingConfigProvider({ children }: ProjectAppBrandingConfigProviderProps): React.ReactNode {
  const projectAppBranding = useProjectAppBranding();
  const configurationQueries = useConfigurationQueries();
  const {
    data: listAppBrandingConfigs = [],
    isPending: isPendingListAppBrandingConfigs,
    isError: isErrorListAppBrandingConfigs,
  } = useQuery(configurationQueries.listAppBrandings());

  const appBrandingConfig = listAppBrandingConfigs.find((config) => config.id === projectAppBranding);

  if (isPendingListAppBrandingConfigs) {
    return <StartupLoading />;
  }

  if (isErrorListAppBrandingConfigs || !appBrandingConfig) {
    return <ConnectionFailurePage />;
  }

  return (
    <ProjectAppBrandingConfigContext value={{ appBrandingConfig }}>
      <DocumentBranding brand={appBrandingConfig.id} />
      {children}
    </ProjectAppBrandingConfigContext>
  );
}
